import type React from 'react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useState } from 'react';

import { useLocation, useNavigate } from '@xing-com/crate-core-router';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import type { EditPagesTracking } from '../../types/tracking';

export type EditContextType = {
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  trackingData: EditPagesTracking | null;
  setTrackingData: React.Dispatch<
    React.SetStateAction<EditPagesTracking | null>
  >;
};

export const EditContext = createContext<EditContextType | undefined>(
  undefined
);

export const EditContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pageContext } = usePageContext();

  const isSearchParamEditOn =
    new URLSearchParams(location.search).get('edit') === 'true';
  const isEditable = pageContext.isEditor;

  const defaultIsEditing = isEditable && isSearchParamEditOn;

  const [isEditing, setIsEditing] = useState<boolean>(defaultIsEditing);
  const [trackingData, setTrackingData] = useState<EditPagesTracking | null>(
    null
  );

  const updateIsEditing = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      if (isEditable) {
        const url = new URL(window.location.href);
        const params = url.searchParams;
        if (params.has('edit')) {
          params.delete('edit');
        } else {
          params.set('edit', 'true');
        }
        navigate(`${url.pathname}?${params.toString()}`, { replace: true });
        setIsEditing(value);
      }
    },
    [isEditable]
  );

  return (
    <EditContext.Provider
      value={{
        isEditing,
        setIsEditing: updateIsEditing,
        trackingData,
        setTrackingData,
      }}
    >
      {children}
    </EditContext.Provider>
  );
};
